// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {actionsRolesAllow} from './ActionsRolesAllow';
import type {RoleDescriptionProps} from './RoleDescription';

const connectDisputeAnalystMessages = defineMessages({
  roleName: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectDisputeAnalyst.roleName',
    description: 'The name of the connect dispute analyst role.',
    defaultMessage: 'Connect Dispute Analyst',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectDisputeAnalyst.shortDescription',
    description: 'The short description of the connect dispute analyst role.',
    defaultMessage:
      'Can view, submit, and accept disputes on connected accounts',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectDisputeAnalyst.descriptionOfWhatRoleCanDo',
    description:
      'The description of what the connect dispute analyst role can do.',
    defaultMessage:
      'This role is for people who need to view, submit evidence for, and accept disputes for connected accounts.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectDisputeAnalyst.descriptionOfWhatRoleCannotDo',
    description:
      "The description of what the connect dispute analyst role can't do.",
    defaultMessage:
      "They can't do anything on the platform account except view connected accounts.",
  },
});

const connectDisputeAnalystDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...connectDisputeAnalystMessages.roleName} />,
  roleId: 'connect_dispute_analyst',
  isBeta: true,
  shortDescription: (
    <FormattedMessage {...connectDisputeAnalystMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage
      {...connectDisputeAnalystMessages.descriptionOfWhatRoleCanDo}
    />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...connectDisputeAnalystMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputesConnect} />,
    <FormattedMessage {...actionsRolesAllow.viewConnectedAccounts} />,
    <FormattedMessage {...actionsRolesAllow.viewPaymentsOnConnectedAccount} />,
    <FormattedMessage {...actionsRolesAllow.viewProductsOnConnectedAccount} />,
    <FormattedMessage {...actionsRolesAllow.viewCustomersOnConnectedAccount} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputesPlatform} />,
    <FormattedMessage
      {...actionsRolesAllow.createViewRefundPaymentsPlatform}
    />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditDeleteProductsPlatform}
    />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditDeleteCustomersPlatform}
    />,
    <FormattedMessage
      {...actionsRolesAllow.createEditRejectConnectedAccounts}
    />,
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,

    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,

    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.configureAnyProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
};

const connectOnboardingAnalystMessages = defineMessages({
  roleName: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectOnboardingAnalyst.roleName',
    description: 'The name of the connect onboarding analyst role.',
    defaultMessage: 'Connect Onboarding Analyst',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectOnboardingAnalyst.shortDescription',
    description:
      'The short description of the connect onboarding analyst role.',
    defaultMessage: 'Can create and upload documents for connected accounts',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectOnboardingAnalyst.descriptionOfWhatRoleCanDo',
    description:
      'The description of what the connect onboarding analyst role can do.',
    defaultMessage:
      'This role is for people who need to create connected accounts and edit their identity information.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.connectOnboardingAnalyst.descriptionOfWhatRoleCannotDo',
    description:
      "The description of what the connect onboarding analyst role can't do.",
    defaultMessage:
      "They can't do anything on the platform account except view and edit connected accounts.",
  },
});

const connectOnboardingAnalystDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...connectOnboardingAnalystMessages.roleName} />,
  roleId: 'connect_onboarding_analyst',
  isBeta: true,
  shortDescription: (
    <FormattedMessage {...connectOnboardingAnalystMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage
      {...connectOnboardingAnalystMessages.descriptionOfWhatRoleCanDo}
    />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...connectOnboardingAnalystMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.createViewEditConnectedAccounts} />,
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage {...actionsRolesAllow.addPersonToConnectedAccount} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputesBoth} />,
    <FormattedMessage {...actionsRolesAllow.createViewRefundPaymentsBoth} />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditDeleteProductsBoth}
    />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditDeleteCustomersBoth}
    />,
    <FormattedMessage {...actionsRolesAllow.rejectConnectedAccounts} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.configureAnyProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
};

const transferAnalystMessages = defineMessages({
  roleName: {
    id: 'docsApp.components.contrib.roleDescriptions.transferAnalyst.roleName',
    description: 'The name of the transfer analyst role.',
    defaultMessage: 'Transfer Analyst',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.transferAnalyst.shortDescription',
    description: 'The short description of the transfer analyst role.',
    defaultMessage: 'Can transfer money, view balance, and view payouts',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.transferAnalyst.descriptionOfWhatRoleCanDo',
    description: 'The description of what the transfer analyst role can do.',
    defaultMessage:
      'This role is for people who need to transfer funds to connected accounts and view the platform’s balance and historical payouts.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.transferAnalyst.descriptionOfWhatRoleCannotDo',
    description: "The description of what the transfer analyst role can't do.",
    defaultMessage:
      "They can't pay out money to external bank accounts, add or edit bank accounts, or create new connected accounts.",
  },
});

const transferAnalystDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...transferAnalystMessages.roleName} />,
  roleId: 'transfer_analyst',
  isBeta: true,
  shortDescription: (
    <FormattedMessage {...transferAnalystMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage {...transferAnalystMessages.descriptionOfWhatRoleCanDo} />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...transferAnalystMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage {...actionsRolesAllow.viewConnectedAccounts} />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewPayouts} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage
      {...actionsRolesAllow.createEditRejectConnectedAccounts}
    />,
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.configureAnyProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
};

export const allConnectRoleProps = [
  connectDisputeAnalystDescriptionProps,
  connectOnboardingAnalystDescriptionProps,
  transferAnalystDescriptionProps,
];
